import { ArrowBack } from '@mui/icons-material';
import { Button, CircularProgress, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { FlexRow } from 'common/FlexRow';
import { useConfirmDialog } from 'common/hooks/useConfirmDialog';
import { ConfirmationDialog } from 'components/ConfirmDialog';
import { useRoot } from 'components/RootProvider';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useAssets } from '../useAssets';
import { AssetPreview } from './AssetPreview';
import { NewAssetButton } from './NewAssetButton';

type AssetListProps = Record<string, never>;
export const AssetList = observer((props: AssetListProps) => {
  const root = useRoot();
  const { collator } = root;
  const assets = useAssets();

  const history = useHistory();

  // Load default category when AssetList component mounts (only in list view)
  React.useEffect(() => {
    if (!assets.currentCategoryId && root.categories.categories.size > 0) {
      // Load the first available category or null category
      const defaultCategoryId = 'null';
      console.log('AssetList mounted - loading default category:', defaultCategoryId);
      assets.handleLoadAssets(defaultCategoryId);
    }
  }, [assets, root.categories.categories.size]);

  const deleteAsset = (assetId: string) => {
    assets.deleteAsset(assetId);
  };

  const [open, handleInit, handleCancel, handleConfirm] = useConfirmDialog(deleteAsset);

  const categoryAssetIds = assets.assetsByCategory.get(assets.currentCategoryId);

  const [openPub, handleInitPub, handleCancelPub, handleConfirmPub] = useConfirmDialog(async () => {
    return await assets.handlePublishAll();
  });

  const [openSaveAll, handleInitSaveAll, handleCancelSaveAll, handleConfirmSaveAll] = useConfirmDialog(async () => {
    return await assets.handleSaveAll();
  });

  return (
    <Box width='100%' height='100%' display='flex' flexDirection='column'>
      <FlexRow>
        <IconButton
          onClick={() => {
            history.push('/');
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography variant='h2'>Assets</Typography>
      </FlexRow>
      <FlexRow>
        <Typography variant='h4'>{'Press button to create moar ->'}</Typography>
        <NewAssetButton></NewAssetButton>
        <Button variant='contained' color='primary' onClick={handleInitPub}>
          Publish All
        </Button>
        <Button variant='contained' color='primary' onClick={handleInitSaveAll} sx={{ ml: 1 }}>
          Save All
        </Button>
        <ConfirmationDialog
          open={openPub}
          onClose={handleCancelPub}
          onConfirm={handleConfirmPub}
          title={'PUBLISH all assets of this category?'}
          message={
            'Publishing these assets will make them available for people to use in the map maker. Do not do this until youve tested them in the web app! Please be careful! NOTE: this will take some time. Check the console for progress feedback.'
          }
        />

        <ConfirmationDialog
          open={openSaveAll}
          onClose={handleCancelSaveAll}
          onConfirm={handleConfirmSaveAll}
          title={'SAVE all assets of this category?'}
          message={
            'This will save all assets in the current category. This may take some time. Check the console for progress feedback.'
          }
        />

      </FlexRow>
      {root.categories.refreshing ? (
        <CircularProgress variant='indeterminate'></CircularProgress>
      ) : (
        <Tabs
          variant='scrollable'
          onChange={(ev, value) => {
            assets.handleLoadAssets(value);
          }}
          value={assets.currentCategoryId}
        >
          {root.categories.categories.size > 0 &&
            [...root.categories.categories.entries()]
              .sort((a, b) => collator.compare(a[1].name.value, b[1].name.value))
              .map(([id, data]) => {
                return <Tab key={id} value={id ?? 'null'} label={data.name.value} />;
              })}
        </Tabs>
      )}
      <FlexRow sx={{ flexWrap: 'wrap' }}>
        {assets.loadingAssets ? (
          <CircularProgress variant='indeterminate' />
        ) : (
          categoryAssetIds
            ?.slice()
            .map(id => <AssetPreview id={id} key={id} onDelete={handleInit}></AssetPreview>)
        )}
      </FlexRow>
      <ConfirmationDialog
        open={open}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={'Delete asset?'}
        message={'Please confirm'}
      />
    </Box>
  );
});
